import React, { useRef } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Modal, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Skeleton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import LoadingComponent from '../../../../../_commons/LoadingComponent';
import VideoListPagination from '../../common/VideoListPagination';

interface VideoSelectionModalProps {
    open: boolean;
    onClose: () => void;
    currentVodList: any[];
    videoListCount: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    handleSelectedRow: (video: any) => void;
    isLoading: boolean;
    cdn: string;
}

function VideoSelectionModal({
    open,
    onClose,
    currentVodList,
    videoListCount,
    currentPage,
    setCurrentPage,
    handleSelectedRow,
    isLoading,
    cdn
}: VideoSelectionModalProps) {
    const { t } = useTranslation();
    const modalContentRef = useRef<HTMLDivElement>(null);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            disablePortal
        >
            <Box className="video-selection-modal__container">
                {/* Fixed Header */}
                <Box className="video-selection-modal__header">
                    <Typography variant="h5" component="h2" className="video-selection-modal__header-title">
                        {t('vod.manage.editor_tab.select_video')}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        className="video-selection-modal__header-close-button"
                    >
                        <ClearIcon />
                    </IconButton>
                </Box>

                {/* Scrollable Content */}
                <Box className="video-selection-modal__content">
                    {/* Fixed Table Header - Always visible */}
                    <Box className="video-selection-modal__table-header">
                        <TableContainer component={Paper} className="video-selection-modal__table-header-container">
                            <Table className="video-list-table-concat">
                                <TableHead>
                                    <TableRow className="video-selection-modal__table-header-row">
                                        <TableCell className="video-selection-modal__table-header-cell">
                                            동영상
                                        </TableCell>
                                        <TableCell className="video-selection-modal__table-header-cell">
                                            파일명
                                        </TableCell>
                                        <TableCell className="video-selection-modal__table-header-cell">
                                            생성날짜
                                        </TableCell>
                                        <TableCell className="video-selection-modal__table-header-cell">
                                            시간
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Content Area - Changes based on state */}
                    <Box className="video-selection-modal__table-content" ref={modalContentRef}>
                        {isLoading ? (
                            <TableContainer component={Paper} className="video-selection-modal__table-content-container">
                                <Table className="table-video-list-concat" style={{ tableLayout: 'fixed' }}>
                                    <TableBody>
                                        {Array.from({ length: 8 }).map((_, idx) => (
                                            <TableRow key={idx} className="video-selection-modal__skeleton-row">
                                                <TableCell className="video-selection-modal__table-content-cell">
                                                    <Skeleton
                                                        variant="rectangular"
                                                        className="video-selection-modal__skeleton-thumbnail"
                                                    />
                                                </TableCell>
                                                <TableCell className="video-selection-modal__table-content-cell">
                                                    <Skeleton
                                                        variant="text"
                                                        className="video-selection-modal__skeleton-title"
                                                    />
                                                </TableCell>
                                                <TableCell className="video-selection-modal__table-content-cell">
                                                    <Skeleton
                                                        variant="text"
                                                        className="video-selection-modal__skeleton-date"
                                                    />
                                                </TableCell>
                                                <TableCell className="video-selection-modal__table-content-cell">
                                                    <Skeleton
                                                        variant="text"
                                                        className="video-selection-modal__skeleton-duration"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : currentVodList.length > 0 ? (
                            <TableContainer component={Paper} className="video-selection-modal__table-content-container">
                                <Table className="videoListTable concat" style={{ tableLayout: 'fixed' }}>
                                    <TableBody>
                                        {currentVodList.map((el, idx) => (
                                            <TableRow
                                                key={el.id}
                                                className="video-selection-modal__table-content-row"
                                                onClick={(e) => handleSelectedRow(el)}
                                            >
                                                <TableCell className="cell-video video-selection-modal__table-content-cell">
                                                    <Box className="videoThumbnail video-selection-modal__table-content-thumbnail">
                                                        <img
                                                            src={cdn ? `${cdn}/${el.uuid}/thumbnail/tn_1.png` : ''}
                                                            alt="thumbnail"
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell className="cell-filename video-selection-modal__table-content-cell">
                                                    <Typography className='videoTitle video-selection-modal__table-content-title' noWrap>
                                                        {el.realFileName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="cell-date video-selection-modal__table-content-cell video-selection-modal__table-content-date">
                                                    {dayjs(el.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                                                </TableCell>
                                                <TableCell className="cell-duration video-selection-modal__table-content-cell video-selection-modal__table-content-duration">
                                                    {dayjs(dayjs.utc(el.playTime * 1000)).format("HH:mm:ss")}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box className="video-selection-modal__empty-state">
                                <Typography variant="body1" color="text.secondary">
                                    {t('common.no_data')}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Fixed Pagination - Always visible */}
                    <Box className="video-selection-modal__pagination">
                        <VideoListPagination
                            videoListCount={videoListCount}
                            paginate={setCurrentPage}
                            currentPage={currentPage}
                        />
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}

export default VideoSelectionModal; 